import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { callActivarVehiculo, callBorrarVehiculo } from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { entrarALaApp } from '../utils/volver';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MIS VEHÍCULOS — varios registrados, uno conduciendo
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Petición del fundador (1-oct-2026): «muchos rentan vehículo, o a veces lo
 * venden… cada vehículo debe pasar su proceso, para no borrar el que ya está.
 * Uber permite agregar varios y solo le pregunta cuál va a conducir».
 *
 * LO QUE ESTA PANTALLA NO HACE, Y POR QUÉ IMPORTA: no escribe nada. Ni activar,
 * ni borrar, ni mucho menos aprobar. Las reglas de Firestore dejan
 * `users/{uid}/vehiculos` en `allow write: if false` y todo pasa por Cloud
 * Functions. Aquí solo se lee y se piden las cosas.
 *
 * Tres estados y tres pintas distintas, porque confundirlos tiene consecuencias
 * en la calle:
 *   · APROBADO + activo → es el que el pasajero ve. Se marca fuerte.
 *   · APROBADO          → se puede poner a conducir de un toque.
 *   · PENDIENTE         → NO se puede conducir todavía. Se dice por qué y que
 *                         no tiene que hacer nada más que esperar.
 *   · RECHAZADO         → con el motivo que escribió el admin, para que sepa
 *                         qué corregir en vez de reenviar a ciegas.
 */
export default function MisVehiculosScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [vehiculos, setVehiculos] = React.useState<any[] | null>(null);
  const [trabajando, setTrabajando] = React.useState<string | null>(null);

  const recargar = React.useCallback(async () => {
    if (!uid) {
      setVehiculos([]);
      return;
    }
    try {
      const lista = await firestoreService.listarMisVehiculos(uid);
      setVehiculos(lista);
    } catch (e: any) {
      console.warn('[mis-vehiculos] no se pudo leer la lista:', e?.message);
      setVehiculos([]);
    }
  }, [uid]);

  // Se relee cada vez que la pantalla vuelve a estar delante: al volver de
  // agregar uno nuevo tiene que aparecer sin cerrar la app.
  useFocusEffect(
    React.useCallback(() => {
      recargar();
    }, [recargar])
  );

  const avisar = (titulo: string, texto: string) => {
    // `Alert.alert` no pinta NADA en react-native-web: sin esta rama, desde el
    // navegador el botón parecería muerto.
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  const activar = async (v: any) => {
    if (trabajando) return;
    setTrabajando(v.id);
    try {
      await callActivarVehiculo({ id: v.id });
      // El espejo local: `setDriverOnline` publica el vehículo leyendo lo que
      // tiene el teléfono, así que sin esto el chofer se pondría en línea
      // anunciando el vehículo anterior.
      useAppStore.getState().setVehicleInfo({
        brand: v.marca ?? null,
        model: v.modelo ?? null,
        color: v.color ?? null,
        year: v.anio ?? null,
        plate: v.placa ?? null,
      });
      await recargar();
      avisar('Listo', `Ahora estás conduciendo tu ${v.color} ${v.marca} ${v.modelo} (${v.placa}).`);
    } catch (e: any) {
      avisar('No se pudo cambiar', mensajeDeError(e, 'Inténtalo de nuevo en un momento.'));
    } finally {
      setTrabajando(null);
    }
  };

  const borrar = async (v: any) => {
    const confirmar = async () => {
      setTrabajando(v.id);
      try {
        await callBorrarVehiculo({ id: v.id });
        await recargar();
      } catch (e: any) {
        avisar('No se pudo borrar', mensajeDeError(e, 'Inténtalo de nuevo en un momento.'));
      } finally {
        setTrabajando(null);
      }
    };
    const texto = `¿Borrar tu ${v.marca} ${v.modelo} (${v.placa})?\n\nSi vuelves a usarlo tendrás que registrarlo otra vez con su foto y tu selfie.`;
    if (Platform.OS === 'web') {
      if (window.confirm(texto)) confirmar();
      return;
    }
    Alert.alert('Borrar vehículo', texto, [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Borrar', style: 'destructive', onPress: confirmar },
    ]);
  };

  const sello = (v: any) => {
    if (v.estado === 'PENDIENTE') {
      return { texto: 'Esperando aprobación', color: '#B36200', fondo: 'rgba(255,149,0,0.14)' };
    }
    if (v.estado === 'RECHAZADO') {
      return { texto: 'No aprobado', color: '#C62828', fondo: 'rgba(255,59,48,0.12)' };
    }
    if (v.activo) {
      return { texto: 'Conduciendo ahora', color: '#1B7F3B', fondo: 'rgba(34,197,94,0.16)' };
    }
    return { texto: 'Aprobado', color: colors.textSecondary, fondo: 'rgba(127,127,127,0.12)' };
  };

  return (
    <ScreenBackground>
      <PantallaSegura style={styles.safe}>
        <View style={[styles.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity
            onPress={() => (router.canGoBack() ? router.back() : entrarALaApp(router, '/(tabs)/profile'))}
            style={styles.volver}
            accessibilityRole="button"
            accessibilityLabel="Volver"
          >
            <Ionicons name="arrow-back" size={20} color={colors.primary} />
            <Text style={[styles.volverTexto, { color: colors.primary }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.cuerpo}>
          <Text style={[styles.titulo, { color: colors.text }]}>Mis vehículos</Text>
          <Text style={[styles.ayuda, { color: colors.textSecondary }]}>
            Puedes tener varios registrados y elegir con cuál vas a conducir. El que esté activo es el
            que verá tu pasajero cuando llegues a recogerlo.
          </Text>

          {vehiculos === null ? (
            <ActivityIndicator style={{ marginTop: 28 }} color={colors.primary} />
          ) : vehiculos.length === 0 ? (
            <Text style={[styles.vacio, { color: colors.textSecondary }]}>
              Todavía no tienes vehículos registrados aquí.
            </Text>
          ) : (
            vehiculos.map((v) => {
              const s = sello(v);
              const esteTrabaja = trabajando === v.id;
              return (
                <View
                  key={v.id}
                  style={[
                    styles.ficha,
                    { backgroundColor: colors.card, borderColor: v.activo ? '#22C55E' : colors.border },
                    v.activo && styles.fichaActiva,
                  ]}
                >
                  <View style={styles.fichaFila}>
                    {v.fotoTrasera ? (
                      <Image source={{ uri: v.fotoTrasera }} style={styles.miniatura} resizeMode="cover" />
                    ) : (
                      <View style={[styles.miniatura, styles.miniaturaVacia]}>
                        <Ionicons name="car-sport" size={22} color={colors.textSecondary} />
                      </View>
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.descripcion, { color: colors.text }]} numberOfLines={2}>
                        {[v.color, v.marca, v.modelo].filter(Boolean).join(' ')}
                      </Text>
                      <View style={styles.placaMarco}>
                        <Text style={styles.placaTexto}>{v.placa}</Text>
                      </View>
                    </View>
                  </View>

                  <View style={[styles.sello, { backgroundColor: s.fondo }]}>
                    <Text style={[styles.selloTexto, { color: s.color }]}>{s.texto}</Text>
                  </View>

                  {v.estado === 'PENDIENTE' && (
                    <Text style={[styles.nota, { color: colors.textSecondary }]}>
                      Una persona de Go Safely está comparando tu selfie con tu cédula y la placa con la
                      foto. No tienes que hacer nada más: te avisamos en cuanto esté.
                    </Text>
                  )}
                  {v.estado === 'RECHAZADO' && !!v.motivoRechazo && (
                    <Text style={[styles.nota, { color: '#C62828' }]}>{v.motivoRechazo}</Text>
                  )}

                  <View style={styles.acciones}>
                    {v.estado === 'APROBADO' && !v.activo && (
                      <TouchableOpacity
                        onPress={() => activar(v)}
                        disabled={!!trabajando}
                        style={[styles.botonPrincipal, !!trabajando && { opacity: 0.6 }]}
                        accessibilityRole="button"
                        accessibilityLabel={`Conducir mi ${v.marca} ${v.modelo}`}
                      >
                        <Text style={styles.botonPrincipalTexto}>
                          {esteTrabaja ? 'Cambiando…' : 'Conducir este'}
                        </Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      onPress={() => borrar(v)}
                      disabled={!!trabajando}
                      style={[styles.botonBorrar, { borderColor: colors.border }]}
                      accessibilityRole="button"
                      accessibilityLabel={`Borrar mi ${v.marca} ${v.modelo}`}
                    >
                      <Ionicons name="trash-outline" size={16} color="#C62828" />
                      <Text style={styles.botonBorrarTexto}>Borrar</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })
          )}

          <TouchableOpacity
            onPress={() => router.push('/agregar-vehiculo' as any)}
            style={[styles.agregar, { backgroundColor: colors.primary }]}
            accessibilityRole="button"
            accessibilityLabel="Agregar otro vehículo"
          >
            <Ionicons name="add" size={20} color="#FFF" />
            <Text style={styles.agregarTexto}>Agregar otro vehículo</Text>
          </TouchableOpacity>

          <Text style={[styles.nota, { color: colors.textSecondary, marginTop: 14 }]}>
            Cada vehículo pasa por su propia revisión: hace falta una foto de la parte de atrás, donde se
            lea la placa, y una selfie tomada en el momento. Así nadie puede cambiar el carro de tu
            cuenta sin que seas tú.
          </Text>
          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  cabecera: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 8, borderBottomWidth: 1 },
  volver: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
  },
  volverTexto: { fontSize: 14, fontWeight: '600' },
  cuerpo: { padding: 16, paddingBottom: 40 },
  titulo: { fontSize: 24, fontWeight: 'bold', marginBottom: 6 },
  ayuda: { fontSize: 13, lineHeight: 19, marginBottom: 18 },
  vacio: { fontSize: 13, lineHeight: 19, marginBottom: 18 },
  ficha: { borderRadius: 16, borderWidth: 1, padding: 14, marginBottom: 14 },
  fichaActiva: { borderWidth: 2 },
  fichaFila: { flexDirection: 'row', gap: 12, alignItems: 'center' },
  miniatura: { width: 84, height: 62, borderRadius: 10, backgroundColor: '#E5E7EB' },
  miniaturaVacia: { alignItems: 'center', justifyContent: 'center' },
  descripcion: { fontSize: 15, fontWeight: '700', textTransform: 'capitalize' },
  placaMarco: {
    alignSelf: 'flex-start',
    marginTop: 6,
    paddingHorizontal: 11,
    paddingVertical: 4,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: '#1F2937',
    backgroundColor: '#FFFFFF',
  },
  placaTexto: { fontSize: 15, fontWeight: '900', letterSpacing: 1.8, color: '#111827' },
  sello: { alignSelf: 'flex-start', marginTop: 12, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 },
  selloTexto: { fontSize: 12, fontWeight: '700' },
  nota: { fontSize: 11.5, lineHeight: 16, marginTop: 10 },
  acciones: { flexDirection: 'row', gap: 10, marginTop: 12, flexWrap: 'wrap' },
  botonPrincipal: {
    backgroundColor: '#1B7F3B',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
  },
  botonPrincipalTexto: { color: '#FFF', fontWeight: '800', fontSize: 13 },
  botonBorrar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  botonBorrarTexto: { color: '#C62828', fontWeight: '700', fontSize: 13 },
  agregar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
    marginTop: 6,
  },
  agregarTexto: { color: '#FFF', fontWeight: '800', fontSize: 15 },
});
